import type { CrmDataTypeConfig } from "./types";
/**
 * Enum for CRM Import workflow steps
 */
export enum WorkflowStep {
  PROJECT_SETUP = "project-setup",
  DATA_IMPORT = "data-import",
  DATA_PROCESSING = "data-processing",
  DATA_VALIDATION = "data-validation",
  TEST_DATA_RELEASE = "test-data-release",
  SUBMIT_FOR_APPROVAL = "submit-for-approval",
}

/**
 * Status of a workflow step
 */
export enum StepStatus {
  PENDING = "pending",
  ACTIVE = "active",
  COMPLETED = "completed",
}

/**
 * Status of a workflow step relative to current step
 */
export enum WorkflowStepStatus {
  COMPLETED = "completed",
  CURRENT = "current",
  UPCOMING = "upcoming",
}

/**
 * Environment options for data studio jobs
 */
export enum DataStudioEnvironment {
  TEST = "test",
  PRODUCTION = "production",
}

/**
 * Severity levels for validation issues
 */
export enum Severity {
  ERROR = "Error",
  WARNING = "Warning",
}

/**
 * Base route path for CRM import workflow
 */
export const CRM_IMPORT_BASE_ROUTE = "/data-studio/crm-import";

/**
 * Data Studio dashboard route
 */
export const DATA_STUDIO_DASHBOARD_ROUTE = "/data-studio/dashboard";

export const CRM_DATA_TYPE_CONFIG: Record<string, CrmDataTypeConfig> = {
  accounts: {
    displayName: "Accounts",
    toolType: "load_positions_transactions",
    validationKey: "accounts",
    order: 1,
  },
  contacts: {
    displayName: "Contacts",
    toolType: "load_contacts",
    validationKey: "contacts",
    order: 2,
  },
  notes: {
    displayName: "Notes",
    toolType: "load_notes",
    validationKey: "notes",
    order: 3,
  },
  comm_pref: {
    displayName: "Communication Preferences",
    toolType: "load_comm_prefs",
    validationKey: "comm_prefs",
    order: 4,
  },
};

/**
 * Mapping of data types to tool types
 * Used for reverse lookup of tool types from data types
 */
export const DATA_TYPE_TO_TOOL_TYPE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(CRM_DATA_TYPE_CONFIG).map(([key, config]) => [key, config.toolType]),
);

/**
 * Predefined order for tools configuration when sending to API
 */
export const TOOL_ORDER_PRIORITY: Record<string, number> = Object.fromEntries(
  Object.entries(CRM_DATA_TYPE_CONFIG).map(([key, config]) => [key, config.order]),
);

/**
 * Mapping from existing data types to toolsToValidate values
 */
export const DATA_TYPE_TO_TOOLS_TO_VALIDATE_MAP: Record<string, string> = Object.fromEntries(
  Object.entries(CRM_DATA_TYPE_CONFIG).map(([key, config]) => [key, config.validationKey]),
);

/**
 * Mapping from validation enum values (ACCOUNTS, CONTACTS, etc.) to job config tool types (load_positions_transactions, etc.)
 * This is derived from CRM_DATA_TYPE_CONFIG to maintain single source of truth
 */
export const VALIDATION_ENUM_TO_JOB_CONFIG_TOOL_TYPE_MAP: Record<string, string> = {
  // Map display names from CRM_DATA_TYPE_CONFIG to validation enum values, then to tool types
  ACCOUNTS: CRM_DATA_TYPE_CONFIG.accounts.toolType,
  CONTACTS: CRM_DATA_TYPE_CONFIG.contacts.toolType,
  NOTES: CRM_DATA_TYPE_CONFIG.notes.toolType,
  COMM_PREFS: CRM_DATA_TYPE_CONFIG.comm_pref.toolType,
};

/**
 * Data type mapping for CRM import files
 * Auto-derived from CRM_DATA_TYPE_CONFIG for consistency
 */
export const DATA_TYPE_MAPPING: Record<string, string> = Object.fromEntries(
  Object.entries(CRM_DATA_TYPE_CONFIG).map(([key, config]) => [key, config.displayName]),
);

/**
 * Available data types for file selection
 * Auto-derived from CRM_DATA_TYPE_CONFIG and sorted by order
 */
export const DATA_TYPE_OPTIONS = Object.entries(CRM_DATA_TYPE_CONFIG)
  .sort(([, a], [, b]) => a.order - b.order)
  .map(([key, config]) => ({ value: key, label: config.displayName }));
/**
 * Entity type mapping from tool types to display names
 * Maps tool types like "load_notes" to display names like "Notes"
 */
export const TOOL_TYPE_TO_DISPLAY_MAPPING: Record<string, string> = {
  load_contacts: "Contacts",
  load_positions_transactions: "Accounts",
  load_notes: "Notes",
  load_comm_prefs: "Communication Preferences",
};
